// The draft gate's mark, its counts and its filter (stage 3 / C, #332; spec
// §2 "Where the colour shows"). Pure; components/GateMark.jsx draws it.
//
// What a row or the review screen says about a draft (server
// lib/gateStore.js displayedGateState, lib/gateViews.js viewAnswer):
//   ready    gate_color red / yellow / green, with its reasons → a mark
//   hidden   the 1-in-10 green holdout: no colour on the wire → `—`
//   pending  stale (a mark covers it, an older version, no holdout salt) → nothing
//   none     not a gated draft → nothing
//
// Green is the QUIETEST mark on purpose: green with check-circle already means
// "approved" in Peak, and a loud green invites skimming — the one thing that
// breaks stage 4's under-1% bar. So green is white with a --success-line edge
// and a plain check, never check-circle, and the queue offers no green filter.

export const GATE_MARKS = Object.freeze({
  red: Object.freeze({ icon: 'x-circle', label: 'לתיקון' }),
  yellow: Object.freeze({ icon: 'alert', label: 'לבדיקה' }),
  // Owner, 31/10/2026: the green label is תקין, with the plain check (V) — it
  // overrides the spec's עבר בדיקות. Still never check-circle, still the quiet look.
  green: Object.freeze({ icon: 'check', label: 'תקין' }),
});

// The only filters: a green filter beside אשר הכל would be stage 4 before
// the bar is met (spec §2, title row).
export const GATE_FILTERS = Object.freeze(['red', 'yellow']);

// What green's tooltip says it checked (spec §2): the checks ran and passed,
// the rest is still the reviewer's.
export const GREEN_TITLE = 'נבדקו: ספק, נמען, סכומים, מע״מ, מחירים — השאר בבדיקה שלך';
// The review screen's mark is the colour from the moment the draft opened;
// the live chips under it are the truth.
export const REVIEW_MARK_TITLE = 'הצבע כפי שחושב בפתיחת הטיוטה';
export const APPROVE_ALL_FILTERED_TITLE = 'אשר הכל פועל על כל התור — בטל את הסינון';

/** What to draw for a row / an activation answer: { kind: 'mark', color } | { kind: 'hidden' } | null. */
export function gateMarkOf(g) {
  if (!g) return null;
  if (g.gate_state === 'hidden') return { kind: 'hidden' };
  if (g.gate_state === 'ready' && GATE_MARKS[g.gate_color]) return { kind: 'mark', color: g.gate_color };
  return null;
}

/** The desktop tooltip: every reason, worst first (the server's order), or what green checked. */
export function gateTitle(g) {
  const m = gateMarkOf(g);
  if (!m || m.kind !== 'mark') return undefined;
  if (m.color === 'green') return GREEN_TITLE;
  const parts = (Array.isArray(g.gate_reasons) ? g.gate_reasons : []).map(r => r && r.detail).filter(Boolean);
  return parts.length ? parts.join(' · ') : undefined;
}

// The colour each draft last had while it was 'ready', in this tab. A
// decision marks its supplier dirty, so right after אשר והבא the supplier's
// other drafts may read 'pending' until the worker catches up (a few
// milliseconds, longer for a big supplier). Navigation inside a filter goes
// by the last known colour then, so the reds of the supplier being worked
// are not skipped for another supplier's; nothing is DRAWN from it.
//
// The memory belongs to ONE display context — this login, with or without
// the owner's preview (utils/gatePreview.js) — so colours remembered under a
// preview never count, filter or navigate for another login in the same tab,
// nor for the owner once she switches the preview off (Codex review of #343,
// preview delta). A new scope starts empty, and with no filter.
const lastKnown = new Map();
let lastKnownScope = null;
/** The display context the remembered colours belong to. */
export const gateColorScope = (uid, preview) => `${uid ?? 'anon'}|${preview ? 'preview' : 'plain'}`;
/** Start a display context: a different one forgets every colour and the filter. */
export function enterGateColorScope(scope) {
  if (scope === lastKnownScope) return;
  // The tab's first context has nothing earlier to protect against.
  const switching = lastKnownScope !== null;
  lastKnown.clear();
  lastKnownScope = scope;
  if (switching) activeFilter = null;
}
export function rememberGateColors(rows, scope = lastKnownScope) {
  enterGateColorScope(scope);
  for (const d of Array.isArray(rows) ? rows : []) {
    if (!d || d.id == null) continue;
    if (d.gate_state === 'ready' && d.gate_color) lastKnown.set(String(d.id), d.gate_color);
    else if (d.gate_state === 'hidden' || d.gate_state === 'none') lastKnown.delete(String(d.id));
  }
}
/** Tests only. */
export const forgetGateColorsForTests = () => { lastKnown.clear(); lastKnownScope = null; };

/** The colour a filter and a count go by: the shown colour, else the last known one while pending. */
export function filterColorOf(d) {
  if (!d) return null;
  if (d.gate_state === 'ready') return d.gate_color || null;
  if (d.gate_state === 'pending') return lastKnown.get(String(d.id)) || null;
  return null;
}

/** { red, yellow, green } — how many drafts each count shows. Held-out and unknown drafts count nowhere. */
export function gateCounts(drafts) {
  const out = { red: 0, yellow: 0, green: 0 };
  for (const d of Array.isArray(drafts) ? drafts : []) {
    const c = filterColorOf(d);
    if (c in out) out[c] += 1;
  }
  return out;
}

/**
 * The queue inside a filter, its order kept (the supplier order applies
 * inside it, owner answer 6). `keepId`, the draft on screen, always keeps its
 * place, so the next draft is the one after it even when its own colour moved.
 * No filter → the queue as given.
 */
export function filterDraftQueue(queue, filter, keepId = null) {
  const list = Array.isArray(queue) ? queue : [];
  if (!GATE_FILTERS.includes(filter)) return list;
  const keep = keepId == null ? null : String(keepId);
  return list.filter(d => (keep != null && String(d?.id) === keep) || filterColorOf(d) === filter);
}

// The active filter, per tab, in module memory: /drafts sets it and the
// review screen's אשר והבא / דלג / מחק והבא follow it. A reload starts with
// none (a filter left on silently would hide most of the queue).
let activeFilter = null;
export const readGateFilter = () => activeFilter;
export function writeGateFilter(filter) { activeFilter = GATE_FILTERS.includes(filter) ? filter : null; }

// The gate's reasons the review strip has no live chip for (spec §2, review
// screen): they become warning chips, so the strip's green
// "אין תקלות פתוחות" never shows while one is open. The other codes are the
// strip's own checks (utils/reviewIssues.js deriveReviewIssues) and its live
// chips are the truth: a reviewer who fixes them closes them. `price` is one
// of those — the strip's per-line price, discount and invoice-discount chips
// fire at any difference, a superset of the gate's 2%.
const LIVE_IN_STRIP = new Set(['row_total', 'totals', 'price']);
const STRIP_COVERS = (r) => LIVE_IN_STRIP.has(r.code)
  // No supplier, a recipient mismatch, a flagged duplicate and an implausible
  // year are the strip's blocking errors / its recipient chip.
  || (r.code === 'supplier' && r.level === 'red')
  || (r.code === 'recipient' && r.level === 'red')
  || (r.code === 'date' && r.level === 'red');

// A reason that is a fact the reviewer answers by choosing (the supplier, the
// document number, a VAT the reading flagged for a look) closes once its
// field differs from the opening the gate judged. `recipient` (not_found),
// `new_product` and `not_checked` are not fixed on the form and stay open.
// The date window and the VAT against the rate are RULES: they close only
// when the form passes them again (Codex review of #343 — a future date
// changed to another future date must stay open).
const ANSWERED_BY = Object.freeze({
  supplier: ['supplier_id'],
  duplicate: ['doc_num', 'supplier_id'],
  vat: ['vat', 'subtotal', 'total', 'invoice_discount'],
});
const VAT_FLAG_DETAIL = 'מע״מ לבדיקה'; // server draftGate.js checkVat, vat_needs_check
/** The form fields the gate's chips watch, taken from the opening the screen committed. */
export function gateOpeningFields(form) {
  const f = form || {};
  return Object.fromEntries([...new Set(Object.values(ANSWERED_BY).flat())].map(k => [k, f[k] ?? null]));
}
const differs = (a, b) => String(a ?? '').trim() !== String(b ?? '').trim();
/** The reason codes the reviewer has answered on the form since the opening. */
export function answeredGateCodes(opening, form) {
  if (!opening) return [];
  const f = form || {};
  return Object.entries(ANSWERED_BY).filter(([, keys]) => keys.some(k => differs(opening[k], f[k]))).map(([code]) => code);
}

// ── The two yellow rules the strip re-checks live ────────────────────────────
// MUST STAY IN SYNC with server/lib/draftGate.js checkDate (the yellow window:
// after today, or more than DATE_PAST_DAYS before the upload, both as Israeli
// days) and checkVat (the VAT against the restaurant rate after the supplier's
// VAT rule, ₪1 slack). Pinned by gateMark.test.js against the same cases.
export const GATE_DATE_PAST_DAYS = 90;
const DAY_MS = 86400000;
const ddmmyyyyDay = (s) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(s || '').trim());
  if (!m) return null;
  const ms = Date.UTC(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  const d = new Date(ms);
  return d.getUTCDate() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 ? ms / DAY_MS : null;
};
// The Israeli calendar day of an instant, as days since the epoch.
const israelDay = (instant) => {
  if (!(instant instanceof Date) || Number.isNaN(instant.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jerusalem', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(instant);
  const part = (t) => Number(parts.find(x => x.type === t)?.value);
  return Date.UTC(part('year'), part('month') - 1, part('day')) / DAY_MS;
};
// documents.created_at: 'YYYY-MM-DD HH:MM:SS', UTC.
const utcInstant = (s) => (s ? new Date(`${String(s).trim().replace(' ', 'T')}Z`) : null);

/** Whether the gate's yellow date rule still fails for this date. */
export function gateDateOpen(date, { now = new Date(), uploadedAt = null } = {}) {
  const day = ddmmyyyyDay(date);
  if (day == null) return true;
  const today = israelDay(now);
  if (today != null && day > today) return true;
  const uploaded = israelDay(utcInstant(uploadedAt));
  return uploaded != null && uploaded - day > GATE_DATE_PAST_DAYS;
}

const agorot = (n) => Math.round((parseFloat(n) || 0) * 100);
/** Whether the gate's VAT-against-the-rate rule still fails for this form. */
export function gateVatOpen(form, { vatRate = 0, inclusive = false, partial = false } = {}) {
  const rate = parseFloat(vatRate) || 0;
  if (!(rate > 0) || inclusive) return false;
  const f = form || {};
  const subtotal = parseFloat(f.subtotal) || 0;
  const discount = parseFloat(f.invoice_discount) || 0;
  const taxable = subtotal - (f.invoice_discount_type === 'amount' ? discount : subtotal * discount / 100);
  const calc = agorot(taxable * rate / 100);
  const vat = agorot(f.vat);
  if (partial) return vat > calc && vat - calc > 100;
  if (vat === 0 && calc !== 0) return true;
  return Math.abs(vat - calc) > 100;
}

// Where a tap on a gate chip lands: the field that answers it (the review
// form's ri-* ids, like the strip's own chips), so the fix is one tap away.
const GATE_TARGETS = Object.freeze({
  supplier: 'ri-supplier', date: 'ri-date', duplicate: 'ri-doc_num', vat: 'ri-vat', new_product: 'ri-items',
});

/**
 * Warning chips for the gate's extra reasons that are still open, judged
 * against the form on screen:
 *   opening            the fields of the first opening this session judged (gateOpeningFields)
 *   form               the form now
 *   now, uploadedAt    the date rule's today and the draft's created_at
 *   vatRate, vatRule   the VAT rule ({ inclusive, partial })
 *   duplicateShown / vatShown   the strip's own duplicate error / VAT chip is up
 *                               (same message, with the one-tap fix): no second chip
 *   deliveryNotesSettled  the strip's own delivery-note check has answered, or no
 *                      longer applies (the type was corrected, e.g. to a מרכזת); while
 *                      it is loading or its lookup failed, the gate's reason stays
 * @returns {Array<{ id, type, severity: 'warning', label, targetId? }>}
 */
export function gateStripIssues(reasons, ctx = {}) {
  const { opening = null, form = null, duplicateShown = false, vatShown = false, deliveryNotesSettled = false } = ctx;
  const answered = new Set(answeredGateCodes(opening, form));
  const stillOpen = (r) => {
    if (STRIP_COVERS(r)) return false;
    switch (r.code) {
      case 'delivery_notes': return !deliveryNotesSettled;
      case 'date': return !form || gateDateOpen(form.date, ctx);
      case 'vat':
        // The strip's own VAT chip says it with the one-tap fix: one warning
        // per problem, the flagged VAT included (Codex review of #343, round 2).
        if (vatShown) return false;
        if (r.detail === VAT_FLAG_DETAIL) return !answered.has('vat');
        return !form || gateVatOpen(form, { vatRate: ctx.vatRate, ...(ctx.vatRule || {}) });
      case 'duplicate': return !duplicateShown && !answered.has('duplicate');
      case 'supplier': return !answered.has('supplier');
      default: return true;
    }
  };
  return (Array.isArray(reasons) ? reasons : [])
    .filter(r => r && r.detail && stillOpen(r))
    .map(r => {
      const targetId = r.code === 'not_checked' ? (/סכומים/.test(r.detail) ? 'ri-total' : 'ri-items') : GATE_TARGETS[r.code];
      return { id: `gate-${r.code}`, type: 'gate', severity: 'warning', label: r.detail, ...(targetId ? { targetId } : {}) };
    });
}

/** document_approved's gate fields: the colour the reviewer was shown, or null when no mark was. */
export function gateAnalytics(g) {
  const m = gateMarkOf(g);
  return { gate_color: m?.kind === 'mark' ? m.color : null, gate_shown: m?.kind === 'mark' };
}
